import { DOCUMENT } from '@angular/common';
import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  NgZone,
  provideAppInitializer,
  Provider,
  Type,
} from '@angular/core';
import {
  AuthConfig,
  DateTimeProvider,
  DefaultHashHandler,
  HashHandler,
  NullValidationHandler,
  OAuthLogger,
  OAuthModuleConfig,
  OAuthNoopResourceServerErrorHandler,
  OAuthResourceServerErrorHandler,
  OAuthService,
  OAuthStorage,
  SystemDateTimeProvider,
  UrlHelperService,
  ValidationHandler,
} from 'angular-oauth2-oidc';

import { HypecastCredentialsAuthenticationGuard } from './guards/hypecast-credentials-auth.guard';
import { HypecastAuthenticationService } from './services/authentication.service';
import { HypecastOAuthService } from './services/hypecast-oauth.service';
import { createOAuthStorage as createSplitOAuthStorage } from './oauth-storage';

export function createOAuthLogger(): OAuthLogger {
  return console;
}

export function createOAuthStorage(): OAuthStorage {
  return createSplitOAuthStorage();
}

/**
 * Identity-provider traffic intentionally bypasses the application's HTTP
 * interceptor chain. HttpBackend is Angular's explicit boundary for creating a
 * raw HttpClient without API-prefix, application auth, caching, or UI handlers.
 */
export function createOAuthService(
  ngZone: NgZone,
  httpBackend: HttpBackend,
  storage: OAuthStorage,
  validationHandler: ValidationHandler,
  urlHelper: UrlHelperService,
  logger: OAuthLogger,
  crypto: HashHandler,
  document: Document,
  dateTimeService: DateTimeProvider,
): OAuthService {
  return new OAuthService(
    ngZone,
    new HttpClient(httpBackend),
    storage,
    validationHandler,
    new AuthConfig(),
    urlHelper,
    logger,
    crypto,
    document,
    dateTimeService,
  );
}

export function createHypecastAuthenticationProviders(
  validationHandlerClass: Type<ValidationHandler> = NullValidationHandler,
): Provider[] {
  return [
    {
      provide: OAuthService,
      useFactory: createOAuthService,
      deps: [
        NgZone,
        HttpBackend,
        OAuthStorage,
        ValidationHandler,
        UrlHelperService,
        OAuthLogger,
        HashHandler,
        DOCUMENT,
        DateTimeProvider,
      ],
    },
    UrlHelperService,
    HypecastOAuthService,
    HypecastCredentialsAuthenticationGuard,
    HypecastAuthenticationService,
    { provide: OAuthLogger, useFactory: createOAuthLogger },
    { provide: OAuthStorage, useFactory: createOAuthStorage },
    { provide: ValidationHandler, useClass: validationHandlerClass },
    { provide: HashHandler, useClass: DefaultHashHandler },
    { provide: DateTimeProvider, useClass: SystemDateTimeProvider },
    {
      provide: OAuthResourceServerErrorHandler,
      useClass: OAuthNoopResourceServerErrorHandler,
    },
    {
      provide: OAuthModuleConfig,
      useValue: { resourceServer: { sendAccessToken: true } } as OAuthModuleConfig,
    },
  ];
}

export function provideHypecastAuthentication(
  validationHandlerClass: Type<ValidationHandler> = NullValidationHandler,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    ...createHypecastAuthenticationProviders(validationHandlerClass),
    provideAppInitializer(() => void inject(HypecastOAuthService)),
  ]);
}
